import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';

/**
 * Group Panel Component
 *
 * Displays currently active grouping fields and allows ungrouping.
 * Future enhancement: Add drag & drop support for columns.
 *
 * @example
 * ```html
 * <app-group-panel
 *   [groupByFields]="['warehouse.name', 'statusId']"
 *   [fieldLabels]="fieldLabelMap"
 *   [panelText]="'Arrastra columnas aquí para agrupar'"
 *   [allowUngroup]="true"
 *   (groupByChanged)="onGroupByChanged($event)">
 * </app-group-panel>
 * ```
 */
@Component({
  selector: 'app-group-panel',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatChipsModule],
  template: `
    <div class="group-panel-container">
      <!-- Empty State -->
      <div *ngIf="!groupByFields || groupByFields.length === 0" class="group-panel-empty">
        <mat-icon class="empty-icon">filter_list</mat-icon>
        <span class="empty-text">{{ panelText }}</span>
      </div>

      <!-- Active Groups -->
      <div *ngIf="groupByFields && groupByFields.length > 0" class="group-panel-active">
        <mat-icon class="panel-icon">filter_list</mat-icon>

        <span class="panel-label">Agrupar por:</span>

        <mat-chip-set class="group-chips">
          <mat-chip
            *ngFor="let field of groupByFields; let index = index"
            class="group-chip">
            <span class="chip-content">
              {{ getFieldLabel(field) }}
            </span>
            <button
              *ngIf="allowUngroup"
              matChipRemove
              (click)="onRemoveGroup(index)">
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
        </mat-chip-set>

        <!-- Clear All Button -->
        <button
          *ngIf="allowUngroup && groupByFields.length > 1"
          mat-button
          color="warn"
          class="clear-all-button"
          (click)="onClearAll()">
          <mat-icon>clear</mat-icon>
          Limpiar todo
        </button>
      </div>
    </div>
  `,
  styles: [`
    .group-panel-container {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background-color: #f3f4f6;
      border: 1px dashed #d1d5db;
      border-radius: 6px;
      margin-bottom: 12px;
      min-height: 52px;
      transition: all 0.2s ease;

      &:hover {
        border-color: #9ca3af;
        background-color: #f9fafb;
      }

      // Dark mode
      :host-context(.dark) & {
        background-color: #1f2937;
        border-color: #4b5563;

        &:hover {
          border-color: #6b7280;
          background-color: #111827;
        }
      }
    }

    .group-panel-empty {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      justify-content: center;
      color: #9ca3af;

      .empty-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      .empty-text {
        font-size: 14px;
        font-style: italic;
      }

      :host-context(.dark) & {
        color: #6b7280;
      }
    }

    .group-panel-active {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      flex-wrap: wrap;
    }

    .panel-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      color: #6b7280;
      flex-shrink: 0;

      :host-context(.dark) & {
        color: #9ca3af;
      }
    }

    .panel-label {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      flex-shrink: 0;

      :host-context(.dark) & {
        color: #d1d5db;
      }
    }

    .group-chips {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      flex: 1;
    }

    .group-chip {
      background-color: #dbeafe !important;
      color: #1e40af !important;
      border-radius: 16px !important;
      padding: 4px 12px !important;

      .chip-content {
        font-size: 13px;
        font-weight: 500;
      }

      button[matChipRemove] {
        margin-left: 4px;
        opacity: 0.7;

        &:hover {
          opacity: 1;
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }

      :host-context(.dark) & {
        background-color: #1e3a8a !important;
        color: #bfdbfe !important;
      }
    }

    .clear-all-button {
      margin-left: auto;
      flex-shrink: 0;

      mat-icon {
        margin-right: 4px;
      }
    }
  `],
})
export class GroupPanelComponent {
  /**
   * Current group by fields
   */
  @Input() groupByFields: string[] = [];

  /**
   * Map of field names to display labels
   * @example { 'warehouse.name': 'Almacén', 'statusId': 'Estado' }
   */
  @Input() fieldLabels?: Record<string, string>;

  /**
   * Placeholder text when no groups
   * @default 'Arrastra columnas aquí para agrupar'
   */
  @Input() panelText: string = 'Arrastra columnas aquí para agrupar';

  /**
   * Allow removing groups
   * @default true
   */
  @Input() allowUngroup: boolean = true;

  /**
   * Emitted when group by fields change
   */
  @Output() groupByChanged = new EventEmitter<string[]>();

  /**
   * Get display label for a field
   */
  getFieldLabel(field: string): string {
    if (this.fieldLabels && this.fieldLabels[field]) {
      return this.fieldLabels[field];
    }

    // Humanize field name
    return field
      .split('.')
      .pop()!
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Remove a group by index
   */
  onRemoveGroup(index: number): void {
    const newGroups = [...this.groupByFields];
    newGroups.splice(index, 1);
    this.groupByChanged.emit(newGroups);
  }

  /**
   * Clear all groups
   */
  onClearAll(): void {
    this.groupByChanged.emit([]);
  }
}
